import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Youtube, ArrowUp } from 'lucide-react';
import { COMPANY, LOGO_URL } from '../mock';
import { useMakes } from '../context/SettingsContext';

export default function Footer() {
  const MAKES = useMakes();
  return (
    <footer className="bg-black text-white relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#e11d2c] to-transparent" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10">
                <img src={LOGO_URL} alt="Timeless" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display text-xl font-bold">Timeless <span className="text-[#e11d2c]">Auto</span> Imports</div>
                <div className="text-[10px] tracking-[0.16em] font-semibold text-white/55 uppercase mt-0.5">Don’t Just Dream it, Drive it</div>
              </div>
            </div>
            <p className="text-white/65 leading-relaxed text-[14px] max-w-md">
              Jamaica’s one-stop automotive shop — Japanese imports, mechanical & hybrid service, body work, parts, and accessories. Lot 36, Bustamante Highway, Four Path, Clarendon.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href={COMPANY.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 grid place-items-center rounded-full border border-white/15 hover:border-[#e11d2c] hover:bg-[#e11d2c] transition-colors"><Instagram size={16} /></a>
              <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 grid place-items-center rounded-full border border-white/15 hover:border-[#e11d2c] hover:bg-[#e11d2c] transition-colors"><Facebook size={16} /></a>
              <a href={COMPANY.social.youtube} target="_blank" rel="noreferrer" className="w-10 h-10 grid place-items-center rounded-full border border-white/15 hover:border-[#e11d2c] hover:bg-[#e11d2c] transition-colors"><Youtube size={16} /></a>
            </div>
            <div className="mt-4 text-[12px] text-white/60">{COMPANY.instagram}</div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#e11d2c] font-semibold mb-5">Explore</div>
            <ul className="space-y-3 text-[14px] text-white/75">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><Link to="/inventory" className="hover:text-white">Inventory</Link></li>
              <li><Link to="/parts" className="hover:text-white">Parts &amp; Accessories</Link></li>
              <li><Link to="/services" className="hover:text-white">Services</Link></li>
              <li><Link to="/notices" className="hover:text-white">Notice Board</Link></li>
              <li><Link to="/locations" className="hover:text-white">Locations we serve</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/track-order" className="hover:text-white">Track an order</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#e11d2c] font-semibold mb-5">Brands</div>
            <ul className="grid grid-cols-2 gap-y-3 gap-x-2 text-[14px] text-white/75">
              {MAKES.map(m => (
                <li key={m}><Link to={`/inventory?make=${encodeURIComponent(m)}`} className="hover:text-white">{m}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#e11d2c] font-semibold mb-5">Visit Us</div>
            <ul className="space-y-4 text-[14px] text-white/80">
              <li className="flex items-start gap-3"><MapPin size={16} className="text-[#e11d2c] mt-0.5" /> <span>{COMPANY.address}</span></li>
              <li className="flex items-start gap-3"><Phone size={16} className="text-[#e11d2c] mt-0.5" /> <a href={`tel:${COMPANY.phone}`} className="hover:text-white">{COMPANY.phone}</a> <span className="text-white/40">/</span> <a href={`tel:${COMPANY.phone2}`} className="hover:text-white">{COMPANY.phone2}</a></li>
              <li className="flex items-start gap-3"><Mail size={16} className="text-[#e11d2c] mt-0.5" /> <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">{COMPANY.email}</a></li>
              <li className="flex items-start gap-3"><Clock size={16} className="text-[#e11d2c] mt-0.5" /> <span>{COMPANY.hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/55 text-[12px]">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-[12px] text-white/55">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-1.5 hover:text-white">
              Back to top <ArrowUp size={13} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
